import { Dispatch } from 'redux';
import { toast } from 'react-toastify';

import { createAction, createAsyncAction } from 'typesafe-actions';
import { UpdateUserInfo, UserActionTypes } from './types';
import {
  IAuthenticationRequestDto,
  IAuthenticationResponseDto,
  ICreateNewUserDto,
  ICreateNewUserResponseDto,
  IForgotPasswordDto,
  IResetPasswordDto,
  IValidateResetTokenDto,
  UserDto,
} from '../../global/dataTransferObjects/user';

import createUserService from '../../services/user';
import portalAuthService, { IPortalDenial, IPortalSession } from '../../services/auth/portalAuthService';
import { persistLegacySession, refreshSession } from '../../services/auth/sessionRefresh';

import { createAuthenticationProvider } from '../../services/providers/factories';
import { AuthenticationKeys } from '../../services/providers/authentication';
import { requiresCompanyChoice } from '../../global/companies';
import {IConfigData} from "../config/types";

const getSignUpMessageByStatus = (status: number): string => {
  switch (status) {
    case 201:
      return 'Usuario criado com sucesso! Efetue o login agora.';
    case 400:
      return 'Informações para cadastro de usuário inválidas!';
    case 409:
      return 'Já existe um usuário com esse email ou número de documento.';
    default:
      return 'Houve um erro ao efetuar seu cadastro!';
  }
};

const getSignInMessageByStatus = (status: number): string => {
  switch (status) {
    case 401:
      return 'Email ou senha inválidos.';
    default:
      return 'Houve um erro ao efetuar o login!';
  }
};

export const updateUserInfo = createAction(UserActionTypes.UpdateUserInfo)<UpdateUserInfo>();

export const doLogout = () => (dispatch: Dispatch): void => {
  const authenticationProvider = createAuthenticationProvider();
  const refreshToken = authenticationProvider.getAuthenticationRefreshToken();

  // Encerra a sessão no VAULT sem aguardar: a resposta é 204 em qualquer caso, e
  // segurar a saída do usuário por causa dela não traz nada. Se a chamada falhar,
  // a sessão morre sozinha no teto de 1 h.
  if (refreshToken) {
    portalAuthService.logout(refreshToken).catch(() => undefined);
  }

  authenticationProvider.clearAuthenticationData();

  dispatch({ type: UserActionTypes.Logout });
};

export const authenticateUserActions = createAsyncAction(
  UserActionTypes.AuthenticateRequest,
  UserActionTypes.AuthenticateSuccess,
  UserActionTypes.AuthenticateFailure,
)<boolean, IAuthenticationResponseDto, string>();

export const createNewUserActions = createAsyncAction(
  UserActionTypes.CreateNewUserRequest,
  UserActionTypes.CreateNewUserSuccess,
  UserActionTypes.CreateNewUserFailure,
)<boolean, ICreateNewUserResponseDto, string>();

export const forgotPasswordActions = createAsyncAction(
  UserActionTypes.ForgotPasswordRequest,
  UserActionTypes.ForgotPasswordSuccess,
  UserActionTypes.ForgotPasswordFailure,
)<boolean, ICreateNewUserResponseDto, string>();

export const resetPasswordActions = createAsyncAction(
  UserActionTypes.ResetPasswordRequest,
  UserActionTypes.ResetPasswordSuccess,
  UserActionTypes.ResetPasswordFailure,
)<boolean, ICreateNewUserResponseDto, string>();

export const validateResetTokenActions = createAsyncAction(
  UserActionTypes.ValidateResetTokenRequest,
  UserActionTypes.ValidateResetTokenSuccess,
  UserActionTypes.ValidateResetTokenFailure,
)<boolean, ICreateNewUserResponseDto, string>();

/**
 * Recompõe a sessão na subida do app renovando o token.
 *
 * O perfil e a lista de empresas não são recarregados: eles vêm do login e já
 * estão em localStorage. O que expira aqui é a credencial, não o cadastro.
 */
export const liftSession = () => async (dispatch: Dispatch): Promise<boolean> => {
  const authProvider = createAuthenticationProvider();

  if (!authProvider.getAuthenticationRefreshToken()) {
    doLogout()(dispatch);
    return true;
  }

  if (!await refreshSession()) {
    doLogout()(dispatch);
    return true;
  }

  const storedUser = localStorage.getItem(AuthenticationKeys.UserInfo);

  if (!storedUser) {
    doLogout()(dispatch);
    return true;
  }

  dispatch(authenticateUserActions.success({
    userInfo: JSON.parse(storedUser),
  } as IAuthenticationResponseDto));

  return true;
};

/**
 * Autentica pelo VAULT, através do BFF.
 *
 * O par de tokens vem do VAULT; userInfo e businessList continuam vindo da base
 * legada, montados pelo BFF — o VAULT ainda não guarda tipo de usuário nem as
 * flags de produto da empresa, e é delas que o portal decide o que mostrar.
 */
export const authenticateUser = (userInfo: IAuthenticationRequestDto) => async (dispatch: Dispatch): Promise<void> => {
  const authProvider = createAuthenticationProvider();
  dispatch(authenticateUserActions.request(true));

  let session: IPortalSession;

  try {
    session = await portalAuthService.login(userInfo.email ?? '', userInfo.password ?? '');
  } catch (error: any) {
    // O VAULT diz por que recusou — conta bloqueada, sem perfil no portal, sem
    // vínculo comercial vigente. Mostrar isso evita que o usuário fique tentando
    // a senha certa numa conta que não vai entrar de jeito nenhum.
    const denial = error?.response?.data as IPortalDenial | undefined;
    const message = denial?.message ?? getSignInMessageByStatus(error?.response?.status ?? 0);

    toast.error(message);
    await dispatch(authenticateUserActions.failure(message));
    return;
  }

  const domain = window.location.hostname;
  const rawConfig = localStorage.getItem(`config_${domain}`);
  const cachedConfig: IConfigData | null = rawConfig ? JSON.parse(rawConfig) : null;
  const domainWhiteLabelId = cachedConfig?.id ?? null;

  const userBusinessData = session.businessList;
  let userBusinessDataWhiteLabelId: number | null = null;

  if (userBusinessData && userBusinessData.length >= 1) {
    if (userBusinessData.length === 1) {
      userBusinessDataWhiteLabelId = userBusinessData[0]?.whiteLabelId ?? null;
    } else {
      const foundElement = userBusinessData.find((element: any) => element?.whiteLabelId !== undefined);
      userBusinessDataWhiteLabelId = foundElement?.whiteLabelId ?? null;
    }
  }

  // Hosts de teste não têm whitelabel próprio que corresponda ao do cliente: o
  // cadastro aponta para o domínio de produção da marca. Casar domínio contra
  // whitelabel aqui recusaria toda conta real — é justamente o que precisa ser
  // testado antes de subir. A checagem existe para impedir que o cliente de uma
  // marca entre pelo portal de outra em produção, e continua valendo lá.
  const TEST_HOSTS = ['localhost', '127.0.0.1', 'portalhml.valori.com.vc'];
  const isTestHost = TEST_HOSTS.includes(domain);

  // HOTFIX: o whitelabel SEKAI (31) não tem portal próprio — foi cadastrado com o
  // mesmo domínio da PagPrime (portal.pagprime.com.br), que resolve para o 24.
  // Sem esta exceção, todo cliente SEKAI recebe "Domínio inválido" no único
  // portal que tem. Chave: whitelabel do domínio; valor: whitelabels aceitos a mais.
  // Vale nos dois sentidos porque o /whitelabel pode devolver os dois cadastros para o
  // domínio e o App.tsx fica com o primeiro — a ordem não é garantida.
  // Remover quando o SEKAI tiver domínio próprio.
  const EXTRA_WHITELABELS_BY_DOMAIN: Record<number, number[]> = {
    24: [31],
    31: [24],
  };
  const isAllowedWhiteLabel = domainWhiteLabelId === userBusinessDataWhiteLabelId
    || (EXTRA_WHITELABELS_BY_DOMAIN[domainWhiteLabelId as number] ?? [])
      .includes(userBusinessDataWhiteLabelId as number);

  if (!isTestHost && userBusinessDataWhiteLabelId && domainWhiteLabelId !== null) {
    if (!isAllowedWhiteLabel && userInfo.grantType !== 'support_access') {
      toast.error("Domínio inválido");
      await dispatch(authenticateUserActions.failure("Domínio inválido"));
      return;
    }
  }

  // O modal pós-login só faz sentido depois de a empresa estar definida — ele
  // consulta a elegibilidade do NPS por ela. Quando há escolha a fazer, quem
  // dispara é a tela de seleção; quando não há, é aqui. O critério era "tem ao
  // menos uma empresa", e por isso a sessão de uma empresa só, que já entra
  // resolvida, nunca chegava a ver o modal.
  const requiresCompanySelection = requiresCompanyChoice(
    session.userInfo?.userType, session.businessList);

  const authData = {
    token: session.token,
    refreshToken: session.refreshToken,
    userInfo: session.userInfo,
    businessList: session.businessList,
  } as unknown as IAuthenticationResponseDto;

  authProvider.setAuthenticationInfo(authData);
  persistLegacySession(session);
  localStorage.setItem(AuthenticationKeys.GrantType, userInfo.grantType ?? '');
  dispatch(authenticateUserActions.success(authData));

  if (!requiresCompanySelection) {
    dispatch(setJustLoggedIn(true));
  }
};

/**
 * Acesso de suporte: troca a sessão do operador pela do usuário informado.
 *
 * O e-mail do operador sai da sessão corrente, e a senha é a dele — quem não
 * souber a senha não passa, então declarar outro ator não dá acesso a nada. A
 * verificação de domínio white-label não se aplica: o operador é interno e
 * costuma acessar contas de outros domínios.
 */
export const impersonateUser = (
  payload: { targetEmail: string, password: string },
) => async (dispatch: Dispatch): Promise<boolean> => {
  const authProvider = createAuthenticationProvider();
  const storedUser = localStorage.getItem(AuthenticationKeys.UserInfo);
  const actorEmail = storedUser ? (JSON.parse(storedUser) as UserDto)?.email : undefined;

  if (!actorEmail) {
    toast.error('Sessão expirada. Entre novamente para usar o acesso de suporte.');
    return false;
  }

  dispatch(authenticateUserActions.request(true));

  let session: IPortalSession;

  try {
    session = await portalAuthService.impersonate(actorEmail, payload.password, payload.targetEmail);
  } catch (error: any) {
    const denial = error?.response?.data as IPortalDenial | undefined;
    const message = denial?.message ?? getSignInMessageByStatus(error?.response?.status ?? 0);

    toast.error(message);
    await dispatch(authenticateUserActions.failure(message));
    return false;
  }

  // Limpa antes de gravar: sem isso a empresa selecionada e os tokens do operador
  // sobreviveriam dentro da sessão do usuário acessado.
  authProvider.clearAuthenticationData();

  const authData = {
    token: session.token,
    refreshToken: session.refreshToken,
    userInfo: session.userInfo,
    businessList: session.businessList,
  } as unknown as IAuthenticationResponseDto;

  authProvider.setAuthenticationInfo(authData);
  persistLegacySession(session);
  localStorage.setItem(AuthenticationKeys.GrantType, 'support_access');
  dispatch(authenticateUserActions.success(authData));

  if (!requiresCompanyChoice(session.userInfo?.userType, session.businessList)) {
    dispatch(setJustLoggedIn(true));
  }

  return true;
};

export const createNewUser = (userInfo: ICreateNewUserDto) => async (dispatch: Dispatch): Promise<boolean> => {
  const userService = createUserService();
  dispatch(createNewUserActions.request(true));
  const createdUser = await userService.createNewUser(userInfo);
  const message = getSignUpMessageByStatus(createdUser.status);

  if (!createdUser || createdUser.status !== 201) {
    toast.error(createdUser.message ?? message);
    dispatch(createNewUserActions.failure(createdUser.message ?? message));
    return false;
  }

  toast.success(createdUser.message ?? message);
  dispatch(createNewUserActions.success(createdUser.response));
  return true;
}

const RESET_REQUEST_MESSAGE =
  'Se este e-mail estiver cadastrado, você receberá as instruções de redefinição em instantes.';

/**
 * Pede o e-mail de redefinição pelo VAULT, através do BFF.
 *
 * A mensagem de sucesso é a mesma existindo ou não o e-mail: o endpoint responde
 * 202 sempre, justamente para não virar oráculo de quais contas existem (RN60).
 * Distinguir os dois casos na tela desfaria isso.
 */
export const forgotPassword = (userInfo: IForgotPasswordDto) => async (dispatch: Dispatch): Promise<void> => {
  dispatch(forgotPasswordActions.request(true));

  try {
    await portalAuthService.requestPasswordReset(userInfo.email);
  } catch {
    toast.error('Erro ao solicitar redefinição de senha, tente mais tarde!');
    dispatch(forgotPasswordActions.failure('Erro ao solicitar redefinição de senha, tente mais tarde!'));
    return;
  }

  toast.success(RESET_REQUEST_MESSAGE);
  dispatch(forgotPasswordActions.success({} as ICreateNewUserResponseDto));
}

export const resetPassword = (userInfo: IResetPasswordDto) => async (dispatch: Dispatch): Promise<boolean> => {
  dispatch(resetPasswordActions.request(true));

  if (userInfo.password !== userInfo.passwordConfirmation) {
    toast.error('As senhas não conferem.');
    dispatch(resetPasswordActions.failure('As senhas não conferem.'));
    return false;
  }

  try {
    await portalAuthService.resetPassword(String(userInfo.token ?? ''), userInfo.password);
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message
      ?? 'Link de redefinição inválido ou expirado. Peça um novo na tela de login.';

    toast.error(errorMessage);
    dispatch(resetPasswordActions.failure(errorMessage));
    return false;
  }

  toast.success('Senha definida. Agora você já pode entrar.');
  dispatch(resetPasswordActions.success({} as ICreateNewUserResponseDto));
  return true;
}

/**
 * O VAULT não expõe validação isolada do token: ele é conferido na hora de
 * gravar a senha. Confirmar antes diria a quem tem um token qualquer se ele
 * existe, sem custo nenhum — a tela abre e o erro, se houver, vem no envio.
 */
export const validateResetToken = (userInfo: IValidateResetTokenDto) => async (dispatch: Dispatch): Promise<boolean> => {
  dispatch(validateResetTokenActions.request(true));
  dispatch(validateResetTokenActions.success({} as ICreateNewUserResponseDto));

  return Boolean(userInfo.token);
}

export const setJustLoggedIn = createAction(
  UserActionTypes.SetJustLoggedIn
)<boolean>();
